import { AxiosProgressEvent } from "axios";
import { type ClassValue, clsx } from "clsx";
import { addSeconds, format, parseISO } from "date-fns";
import { twMerge } from "tailwind-merge";

import { AnalyticsPeriod } from "@/data/types";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function convertTimeFromSeconds(seconds: number) {
  const time = addSeconds(new Date(0), seconds);
  return format(time, "HH:mm:ss");
}

export function formatDuration(seconds: number | undefined): string {
  // Handle undefined, zero or negative values
  if (!seconds || seconds <= 0) {
    return "0s";
  }

  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);

  // Build the format string based on what components we have
  const parts: string[] = [];

  if (days > 0) {
    parts.push(`${days}d`);
  }

  if (hours > 0) {
    parts.push(`${hours}h`);
  }

  if (minutes > 0) {
    parts.push(`${minutes}m`);
  }

  if (remainingSeconds > 0) {
    parts.push(`${remainingSeconds}s`);
  }

  // Join all parts with spaces
  return parts.join(" ");
}

export function formatByPeriod(value: string, period?: AnalyticsPeriod) {
  try {
    const date = parseISO(value);

    switch (period) {
      case AnalyticsPeriod.MONTH:
        return new Intl.DateTimeFormat(undefined, {
          month: "short",
          year: "numeric",
        }).format(date);
      case AnalyticsPeriod.WEEK:
        return new Intl.DateTimeFormat(undefined, {
          month: "short",
          day: "numeric",
          year: "numeric",
        }).format(date);
      case AnalyticsPeriod.DAY:
        return new Intl.DateTimeFormat(undefined, {
          month: "short",
          day: "numeric",
        }).format(date);
      default:
      case AnalyticsPeriod.HOUR:
        return new Intl.DateTimeFormat(undefined, {
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "numeric",
        }).format(date);
    }
  } catch (exception) {
    return value;
  }
}

export type UploadProgressEvent = (
  uploadedBytes: number,
  totalBytes: number,
) => void;

export const wrapAxiosProgress =
  (onUploadProgress: UploadProgressEvent) => (event: AxiosProgressEvent) => {
    if (event.lengthComputable && event.total) {
      return onUploadProgress(event.loaded, event.total);
    }
  };

type Matrix<T> = Array<T[]>;

export const upcFormatter = (value: string, groups: number[]): string => {
  let cursor = 0;
  const digits = value.replace(/\D/g, "");

  const pairs: Matrix<number> = groups.reduce<Matrix<number>>((list, size) => {
    if (cursor >= digits.length) {
      return list;
    }

    const pair = [cursor, cursor + size];
    cursor += size;
    return [...list, pair];
  }, []);

  return pairs.map((pair) => digits.slice(...pair)).join("-");
};

export const preserveLineBreaks = (text?: string) => {
  if (!text) return "";
  const lines = text.split("\n");

  return lines
    .map((line, index): string => {
      // Check if the line is part of a list
      const isListItem = /^\s*[*\-+]\s+|^\s*\d+\.\s+/.test(line);
      const isNextLineListItem =
        index < lines.length - 1 &&
        /^\s*[*\-+]\s+|^\s*\d+\.\s+/.test(lines[index + 1]);

      if (isListItem || isNextLineListItem) return line;

      if (line.trim() === "\\") return line.replace("\\", "&nbsp;\n");

      return line + "&nbsp;\n";
    })
    .join("\n");
};
